import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { ArrowRight, Box, Building2, Home, Layers3, Search } from "lucide-react";

import appCss from "../styles.css?url";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import logo from "@/assets/inext-logo.svg";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { getMessages } from "@/lib/i18n";
import { loadAnnouncement } from "@/lib/announcement-loader";
import { isLocale, localizeUrl, resolveSiteContext } from "@/lib/site";

// Umami analytics, baked in at build time (Vite inlines VITE_*) — only the
// production build sets these (Makefile `build-prod`), so it ships on production
// only, never staging/dev. The script is served first-party (VITE_UMAMI_SRC =
// /s/script.js, proxied to Umami by nginx) so adblockers see a same-origin
// request, and the beacon goes to VITE_UMAMI_HOST_URL (also first-party).
const umamiEnv = import.meta.env as unknown as Record<string, string | undefined>;
const UMAMI_SRC = umamiEnv.VITE_UMAMI_SRC;
const UMAMI_WEBSITE_ID = umamiEnv.VITE_UMAMI_WEBSITE_ID;
const UMAMI_HOST_URL = umamiEnv.VITE_UMAMI_HOST_URL;

// We load Umami via a tiny inline bootstrap rather than a static <script src>
// so we can honour the visitor's privacy signal: if Global Privacy Control or
// Do Not Track is set, we don't load the tracker at all. That check is
// runtime-only (navigator isn't available during SSR), hence the inline script.
const umamiBootstrap =
  UMAMI_SRC && UMAMI_WEBSITE_ID
    ? `(function(){try{var n=navigator;if(n.globalPrivacyControl||n.doNotTrack==="1"||window.doNotTrack==="1")return;}catch(e){}` +
      `var s=document.createElement("script");s.async=true;s.src=${JSON.stringify(UMAMI_SRC)};` +
      `s.setAttribute("data-website-id",${JSON.stringify(UMAMI_WEBSITE_ID)});` +
      (UMAMI_HOST_URL ? `s.setAttribute("data-host-url",${JSON.stringify(UMAMI_HOST_URL)});` : "") +
      `document.head.appendChild(s);})();`
    : null;

function NotFoundComponent() {
  const href = useRouterState({ select: (state) => state.location.href });
  const site = resolveSiteContext(href);
  const messages = getMessages(site.locale);
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const destinations = [
    { label: messages.navProducts, href: u("/products/inext-tv6-ultra"), Icon: Box },
    { label: messages.navSolutions, href: u("/solutions/alcatraz-dms"), Icon: Layers3 },
    { label: messages.navAbout, href: u("/about"), Icon: Building2 },
    { label: messages.navContact, href: u("/contacts"), Icon: ArrowRight },
  ];

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      <div className="relative isolate min-h-screen overflow-hidden bg-background">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-20 bg-grid opacity-40"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-48 -top-48 -z-10 h-[34rem] w-[34rem] rounded-full opacity-70 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-64 -right-48 -z-10 h-[38rem] w-[38rem] rounded-full opacity-50 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />

        <header className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <a href={u("/")} aria-label={messages.goHome} className="group">
            <img
              src={logo}
              alt="inext"
              className="h-7 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </a>
          <LanguageSwitcher home />
        </header>

        <main className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-7xl items-center px-6 py-12 sm:py-16">
          <div className="grid w-full items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(26rem,0.85fr)] lg:gap-20">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">404</p>
              <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                {messages.notFoundTitle}
              </h1>
              <p className="mt-5 max-w-xl text-pretty text-base leading-7 text-muted-foreground sm:text-lg">
                {messages.notFoundBody}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="h-11 rounded-lg px-5 animate-glow">
                  <a href={u("/")}>
                    <Home className="h-4 w-4" />
                    {messages.goHome}
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-11 rounded-lg bg-background/60 px-5 backdrop-blur-sm"
                >
                  <a href={u("/search")}>
                    <Search className="h-4 w-4" />
                    {messages.navSearch}
                  </a>
                </Button>
              </div>
            </div>

            <nav
              aria-label={messages.notFoundTitle}
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
            >
              {destinations.map(({ label, href: destinationHref, Icon }) => (
                <a
                  key={destinationHref}
                  href={destinationHref}
                  className="group flex min-h-32 flex-col justify-between rounded-2xl border border-border/80 bg-background/70 p-5 shadow-sm backdrop-blur-md transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
                >
                  <span className="flex items-start justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="h-5 w-5" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-primary" />
                  </span>
                  <span className="mt-5 text-sm font-semibold text-foreground">{label}</span>
                </a>
              ))}
            </nav>
          </div>
        </main>
      </div>
    </>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const href = useRouterState({ select: (state) => state.location.href });
  const site = resolveSiteContext(href);
  const messages = getMessages(site.locale);

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {messages.errorTitle}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{messages.errorBody}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => {
                router.invalidate();
                reset();
              }}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {messages.tryAgain}
            </button>
            <a
              href={localizeUrl("/", site.locale, href)}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              {messages.goHome}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async ({ location }) => ({
    announcement: await loadAnnouncement(resolveSiteContext(location.href)),
  }),
  head: ({ match, matches }) => {
    const search = match.search as Record<string, unknown>;
    const requestedLocale = typeof search.site === "string" ? search.site : undefined;
    const locale = isLocale(requestedLocale)
      ? requestedLocale
      : resolveSiteContext(match.pathname).locale;
    const messages = getMessages(locale);
    const isNotFound = matches.some(
      (routeMatch) => routeMatch.status === "notFound" || routeMatch.globalNotFound,
    );
    const hasError = matches.some((routeMatch) => routeMatch.status === "error");
    const title = isNotFound
      ? `${messages.notFoundTitle} | inext`
      : hasError
        ? `${messages.errorTitle} | inext`
        : "inext";
    const description = isNotFound
      ? messages.notFoundBody
      : hasError
        ? messages.errorBody
        : "Set-Top Boxes and device management software for IPTV and OTT providers.";

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title },
        { name: "description", content: description },
        ...(isNotFound || hasError ? [{ name: "robots", content: "noindex, nofollow" }] : []),
      ],
      links: [
        // The self-hosted Inter faces use font-display: swap, so text paints in
        // the fallback immediately — preloading them would only steal early
        // bandwidth from the render-blocking CSS and delay first paint. We let
        // them load at default priority and swap in.
        // The home hero's STB packshot is eager + fetchPriority="high" in the
        // SSR'd HTML, so the preload scanner discovers it during initial parse.
        // Keep route-specific media out of this root link list: an explicit
        // preload here would also run on every non-home route.
        {
          rel: "stylesheet",
          href: appCss,
        },
        {
          rel: "icon",
          type: "image/svg+xml",
          href: "/favicon.svg",
        },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const href = useRouterState({ select: (state) => state.location.href });
  const site = resolveSiteContext(href);
  return (
    <html lang={site.locale}>
      <head>
        <HeadContent />
        {umamiBootstrap ? <script dangerouslySetInnerHTML={{ __html: umamiBootstrap }} /> : null}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { announcement } = Route.useLoaderData();

  return (
    <QueryClientProvider client={queryClient}>
      <AnnouncementBar announcement={announcement} />
      <Outlet />
    </QueryClientProvider>
  );
}
